/**
 * A problem report in the suite's format, `op.report` v1 (2026-10-05).
 *
 * The format is OPApps' (`OPApps/CONTRACTS/op-report.md`, agreed with the
 * operator 2026-10-05); this file only maps OPButtons' own diagnostics onto
 * it. `report.ts` builds the local record (v0, "Send to my computer"); this
 * turns it into what goes to OverpoweredApps, following the contract's
 * mapping: `device` as is; bridges, errors, verdict and history under `data`;
 * the black box text as the attachment `black-box.csv`; a UUID for `id`;
 * `type` and `note` from the person.
 *
 * Diagnostics are optional. An idea or a question needs none of them, and the
 * preview lets the person leave them off; then the report is their words, the
 * app, its build and the device type, and nothing else.
 *
 * Secrets: the v0 record is already stripped (`redactSecrets`), and the note
 * is the person's own text, so it is stripped here too. The server scans
 * again as the backstop.
 */

import { redactSecrets, type DiagReport } from './report.ts';

export type ReportType = 'freeze' | 'bug' | 'idea' | 'question' | 'unspecified';
export type ReportSent = 'manual' | 'auto';

export interface OpReportAttachmentMeta { name: string; type: string; bytes: number }

export interface OpReport {
  schema: 'op.report';
  version: 1;
  id: string;
  app: 'opbuttons';
  build: string;
  /** The version the person sees (op-report.md, added 2026-10-05): what
   *  OPApps' "Fixed in OPButtons <version>" names. NOT `version`, which is
   *  this format's own number. */
  app_version: string;
  at: string;
  type: ReportType;
  note: string;
  page?: string;
  signature?: string;
  sent: ReportSent;
  device: { kind: string; user_agent: string; standalone: boolean; screen: { w: number; h: number; dpr: number } };
  attachments: OpReportAttachmentMeta[];
  data: Record<string, unknown>;
}

/** An attachment's bytes, held as text (every attachment we send is text). */
export interface OpAttachment { name: string; type: string; text: string }

export interface OutgoingReport { report: OpReport; attachments: OpAttachment[] }

/** The contract's limits (op-report.md section 1). */
export const LIMITS = {
  note: 4000,
  reportBytes: 64 * 1024,
  attachmentBytes: 1024 * 1024,
} as const;

export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** A v4 UUID. `randomUUID` exists in every secure context we run in; the
 *  fallback keeps a non-secure dev origin working rather than throwing. */
export function newReportId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Keep the NEWEST end of a text that is over `max` bytes, cut at a line, so
 *  the part nearest the incident survives. */
export function keepTail(text: string, max: number): string {
  if (byteLength(text) <= max) return text;
  let s = text.slice(-Math.floor(max * 0.9));
  const nl = s.indexOf('\n');
  if (nl >= 0) s = s.slice(nl + 1);
  return `[older lines removed to fit ${Math.round(max / 1024)} KB]\n${s}`;
}

/** Freezes group by what the black box says happened, so the inbox shows "the
 *  link dropped" and "iOS stopped the app" as two problems, not one. */
function freezeSignature(verdict: string): string | undefined {
  if (verdict.startsWith('JS STOPPED')) return 'freeze-app-stopped';
  if (verdict.startsWith('JS STAYED ALIVE')) return 'freeze-link-lost';
  if (verdict.startsWith('PAUSED IN THE BACKGROUND')) return 'freeze-backgrounded';
  if (verdict.startsWith('PAGE CLOSED')) return 'freeze-page-closed';
  return undefined;
}

export interface ToOpReportOptions {
  type: ReportType;
  note: string;
  sent: ReportSent;
  /** Include connection history, recent errors and the machines' state. */
  diagnostics: boolean;
  /** Where in the app it happened: a help slug when there is one. */
  page?: string;
}

export function toOpReport(v0: DiagReport, opts: ToOpReportOptions): OutgoingReport {
  const note = redactSecrets(opts.note.trim()).slice(0, LIMITS.note);
  const attachments: OpAttachment[] = [];
  let data: Record<string, unknown> = {};
  if (opts.diagnostics) {
    data = {
      bridges: v0.bridges,
      errors: v0.errors,
      verdict: v0.blackBox.verdict,
      history: v0.blackBox.history,
    };
    attachments.push({ name: 'black-box.csv', type: 'text/csv', text: keepTail(v0.blackBox.text, LIMITS.attachmentBytes) });
  }
  const signature = opts.type === 'freeze' && opts.diagnostics ? freezeSignature(v0.blackBox.verdict) : undefined;
  const report: OpReport = {
    schema: 'op.report',
    version: 1,
    id: newReportId(),
    app: 'opbuttons',
    build: v0.build,
    app_version: __APP_VERSION__,
    at: v0.at,
    type: opts.type,
    note,
    ...(opts.page ? { page: opts.page.slice(0, 64) } : {}),
    ...(signature ? { signature } : {}),
    sent: opts.sent,
    device: {
      kind: v0.device.kind,
      user_agent: v0.device.userAgent,
      standalone: v0.device.standalone,
      screen: v0.device.screen,
    },
    attachments: attachments.map((a) => ({ name: a.name, type: a.type, bytes: byteLength(a.text) })),
    data,
  };
  return { report: fitReport(report), attachments };
}

/** The report JSON must stay under 64 KB without its attachments. Errors are
 *  the only part that can grow: drop their stacks first, then the oldest. */
export function fitReport(report: OpReport): OpReport {
  const size = (r: OpReport) => byteLength(JSON.stringify(r));
  if (size(report) <= LIMITS.reportBytes) return report;
  const errors = Array.isArray(report.data.errors) ? [...(report.data.errors as Array<Record<string, unknown>>)] : [];
  let r: OpReport = { ...report, data: { ...report.data, errors: errors.map((e) => ({ ...e, stack: '' })) } };
  while (size(r) > LIMITS.reportBytes && (r.data.errors as unknown[]).length > 0) {
    r = { ...r, data: { ...r.data, errors: (r.data.errors as unknown[]).slice(1) } };
  }
  return r;
}
